import { gql, makeReference, useMutation } from '@apollo/client';
import { uniq } from 'lodash';
import { useCallback, useState } from 'react';

import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CommentStatus, ProposalStatus } from 'src/lib/graphqlTypes/types';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';

import { API_SCHEMA_ID } from '../../documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';
import { proposalChangesRouteConfig } from '../../routes';
import {
  UseMentionUsersServiceFragment,
  useGetUsersForMentions,
} from '../CommentMarkdownEditor/useMentionUsers';

import {
  NEW_COMMENT_TEMP_ID,
  ProposalCommentDrawerQuery,
  ProposalCommentsParentCommentFragment,
  UseAddCommentNewChangeParentCommentFragment,
  UseAddCommentNewGeneralParentCommentFragment,
  UseAddCommentNewReplyChangeCommentFragment,
  UseAddCommentNewReplyGeneralCommentFragment,
} from './shared';

const userIdMentionRegex = /(^|\s)@(?<id>[^\s',;]+)/g;

export const useAddProposalComment = ({
  graphRef,
  proposal,
  users,
  commentThreadId,
}: {
  graphRef: GraphRef;
  proposal:
    | GraphQLTypes.Maybe<GraphQLTypes.UseAddCommentProposalFragment>
    | undefined;
  users: ReturnType<typeof useGetUsersForMentions>['users'];
  commentThreadId?: string;
}) => {
  const [commentErrorMessage, setCommentErrorMessage] = useState<
    string | null | undefined
  >(null);
  const { linkedCoordinateName } = useRouteParams(
    proposalChangesRouteConfig,
    catchAllRouteConfig,
  );
  const { me } = useIdentity();

  const [addCommentMutation, { loading }] = useMutation<
    GraphQLTypes.UseAddCommentMutation,
    GraphQLTypes.UseAddCommentMutationVariables
  >(
    gql`
      # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
      mutation UseAddCommentMutation(
        $graphId: ID!
        $graphVariant: String!
        $input: AddCommentInput!
      ) {
        graph(id: $graphId) {
          variant(name: $graphVariant) {
            proposal {
              ... on ProposalMutation {
                addComment(input: $input) {
                  ...ProposalCommentsParentCommentFragment
                  ...UseAddCommentNewChangeParentCommentFragment
                  ...UseAddCommentNewGeneralParentCommentFragment
                  ...UseAddCommentNewReplyChangeCommentFragment
                  ...UseAddCommentNewReplyGeneralCommentFragment
                  ... on ValidationError {
                    message
                  }
                  ... on NotFoundError {
                    message
                  }
                }
              }
              ... on ValidationError {
                message
              }
              ... on NotFoundError {
                message
              }
            }
          }
        }
      }
      ${UseAddCommentNewChangeParentCommentFragment}
      ${UseAddCommentNewGeneralParentCommentFragment}
      ${UseAddCommentNewReplyChangeCommentFragment}
      ${UseAddCommentNewReplyGeneralCommentFragment}
      ${ProposalCommentsParentCommentFragment}
    `,
    {
      update: (cache, { data: resultData }) => {
        const proposalId = proposal?.id;
        if (!proposalId) return;
        const resultProposal = resultData?.graph?.variant?.proposal;
        if (resultProposal?.__typename === 'ProposalMutation') {
          const addCommentResult = resultProposal.addComment;
          if (
            addCommentResult.__typename === 'ParentChangeProposalComment' &&
            linkedCoordinateName &&
            proposal?.latestRevision?.id
          ) {
            const currentUseMentionUsersServiceFragment = cache.readFragment({
              id: cache.identify({
                __typename: 'Service',
                id: graphRef.graphId,
              }),
              fragment: UseMentionUsersServiceFragment,
            });

            // Parent change proposal comments only are made on the changes page
            // and affect the ProposalCommentDrawerQuery
            cache.writeQuery({
              query: ProposalCommentDrawerQuery,
              data: {
                service: {
                  // this fragment should always be present since we query for users
                  // to mention before allowing folks to comment every time
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  ...currentUseMentionUsersServiceFragment!,
                  __typename: 'Service',
                  id: graphRef.graphId,
                  variant: {
                    __typename: 'GraphVariant',
                    id: graphRefToString(graphRef),
                    proposal: {
                      status: ProposalStatus.OPEN,
                      __typename: 'Proposal',
                      id: proposalId,
                      comment: {
                        ...addCommentResult,
                        replies: [],
                      },
                      latestRevision: {
                        __typename: 'ProposalRevision',
                        id: proposal?.latestRevision?.id,
                      },
                    },
                  },
                },
              },
              variables: {
                commentId: addCommentResult.id,
                ...graphRef,
                shouldQueryComments: true,
              },
            });
            cache.modify<GraphQLTypes.Proposal>({
              id: cache.identify({ __typename: 'Proposal', id: proposalId }),
              fields: {
                parentComments: (cachedParentComments = [], { readField }) => {
                  const parentCommentId = cache.identify({
                    __typename: 'ParentChangeProposalComment',
                    id: addCommentResult.id,
                  });

                  if (
                    !parentCommentId ||
                    cachedParentComments.some(
                      (ref) => readField('id', ref) === addCommentResult.id,
                    )
                  ) {
                    return cachedParentComments;
                  }
                  const newParentCommentRef = makeReference(parentCommentId);
                  return [...cachedParentComments, newParentCommentRef];
                },
              },
            });
            cache.evict({
              id: cache.identify({
                __typename: 'Proposal',
                id: proposalId,
              }),
              fieldName: 'activities',
            });
          } else if (
            addCommentResult.__typename === 'ParentGeneralProposalComment'
          ) {
            cache.evict({
              id: cache.identify({
                __typename: 'Proposal',
                id: proposalId,
              }),
              fieldName: 'activities',
            });
          } else if (
            addCommentResult.__typename === 'ReplyChangeProposalComment' &&
            commentThreadId
          ) {
            cache.modify<GraphQLTypes.ParentChangeProposalComment>({
              id: cache.identify({
                __typename: 'ParentChangeProposalComment',
                id: commentThreadId,
              }),
              fields: {
                replies: (cachedReplies = [], { readField }) => {
                  const replyId = cache.identify({
                    __typename: 'ReplyChangeProposalComment',
                    id: addCommentResult.id,
                  });

                  if (
                    !replyId ||
                    cachedReplies.some(
                      (ref) => readField('id', ref) === addCommentResult.id,
                    )
                  ) {
                    return cachedReplies;
                  }

                  const newReplyRef = makeReference(replyId);
                  return [...cachedReplies, newReplyRef];
                },
                replyCount: (currentReplyCount: number = 0) =>
                  currentReplyCount + 1,
              },
            });
          } else if (
            addCommentResult.__typename === 'ReplyGeneralProposalComment' &&
            commentThreadId
          ) {
            cache.modify<GraphQLTypes.ParentChangeProposalComment>({
              id: cache.identify({
                __typename: 'ParentGeneralProposalComment',
                id: commentThreadId,
              }),
              fields: {
                replies: (existingReplyRefs = [], { readField }) => {
                  const replyId = cache.identify({
                    __typename: 'ReplyGeneralProposalComment',
                    id: addCommentResult.id,
                  });

                  if (
                    !replyId ||
                    existingReplyRefs.some(
                      (ref) => readField('id', ref) === addCommentResult.id,
                    )
                  ) {
                    return existingReplyRefs;
                  }

                  const newReplyRef = makeReference(replyId);
                  return [...existingReplyRefs, newReplyRef];
                },
                replyCount: (currentReplyCount: number = 0) =>
                  currentReplyCount + 1,
              },
            });
          }
        }
      },
    },
  );

  const addComment = useCallback(
    async ({
      message,
      schemaCoordinate: schemaCoordinateArgument,
      schemaScope: schemaScopeArgument,
    }: {
      message: string;
      schemaCoordinate?: string;
      schemaScope?: string;
    }) => {
      const proposalId = proposal?.id;
      const revisionId = proposal?.latestRevision?.id;
      if (!revisionId || !proposalId) {
        return;
      }
      const schemaCoordinate = schemaCoordinateArgument ?? linkedCoordinateName;
      const schemaScope =
        schemaScopeArgument ?? (schemaCoordinate ? API_SCHEMA_ID : undefined);

      const isChangeComment = !!schemaCoordinate && !!schemaScope;

      const taggedUserIds = uniq(
        [...message.matchAll(userIdMentionRegex)].flatMap((userMatch) =>
          users.some(({ userId }) => userMatch?.groups?.id === userId)
            ? (userMatch.groups?.id ?? [])
            : [],
        ),
      );

      return addCommentMutation({
        variables: {
          graphId: graphRef.graphId,
          graphVariant: graphRef.graphVariant,
          input: {
            message,
            schemaScope,
            schemaCoordinate,
            parentCommentId: commentThreadId,
            revisionId,
            usersToNotify: taggedUserIds,
          },
        },
        optimisticResponse: {
          graph: {
            __typename: 'ServiceMutation',
            variant: {
              __typename: 'GraphVariantMutation',
              proposal: {
                __typename: 'ProposalMutation',
                addComment: commentThreadId
                  ? {
                      __typename: isChangeComment
                        ? 'ReplyChangeProposalComment'
                        : 'ReplyGeneralProposalComment',
                      id: NEW_COMMENT_TEMP_ID,
                      message,
                      createdAt: new Date().toISOString(),
                      createdBy: me,
                      status: CommentStatus.OPEN,
                      updatedAt: null,
                    }
                  : isChangeComment
                    ? {
                        __typename: 'ParentChangeProposalComment',
                        id: NEW_COMMENT_TEMP_ID,
                        message,
                        createdAt: new Date().toISOString(),
                        createdBy: me,
                        replies: [],
                        replyCount: 0,
                        schemaCoordinate,
                        status: CommentStatus.OPEN,
                        updatedAt: null,
                        schemaScope,
                        outdated: false,
                      }
                    : {
                        __typename: 'ParentGeneralProposalComment',
                        id: NEW_COMMENT_TEMP_ID,
                        message,
                        createdAt: new Date().toISOString(),
                        createdBy: me,
                        replies: [],
                        replyCount: 0,
                        status: CommentStatus.OPEN,
                        updatedAt: null,
                      },
              },
            },
          },
        },
      });
    },
    [
      proposal?.id,
      proposal?.latestRevision?.id,
      linkedCoordinateName,
      addCommentMutation,
      graphRef.graphId,
      graphRef.graphVariant,
      commentThreadId,
      me,
      users,
    ],
  );

  const handleAddCommentResult = useCallback(
    ({
      result,
      onError,
      onSuccess,
    }: {
      result: Awaited<ReturnType<typeof addComment>>;
      onError?: () => void;
      onSuccess?: () => void;
    }) => {
      const resultProposal = result?.data?.graph?.variant?.proposal;
      if (
        !result ||
        !resultProposal ||
        resultProposal.__typename !== 'ProposalMutation' ||
        resultProposal.addComment.__typename === 'NotFoundError' ||
        resultProposal.addComment.__typename === 'ValidationError' ||
        !!result.errors
      ) {
        setCommentErrorMessage(() => {
          if (
            resultProposal?.__typename === 'ValidationError' ||
            resultProposal?.__typename === 'NotFoundError'
          ) {
            return resultProposal?.message;
          } else if (resultProposal?.__typename === 'PermissionError') {
            return 'You do not have permission to perform this action.';
          } else if (result?.errors) {
            return 'A network error has ocurred. Please try again later.';
          }
          return undefined;
        });
        onError?.();
      } else if (
        resultProposal?.addComment.__typename ===
          'ParentGeneralProposalComment' ||
        resultProposal?.addComment.__typename ===
          'ParentChangeProposalComment' ||
        resultProposal?.addComment.__typename ===
          'ReplyGeneralProposalComment' ||
        resultProposal?.addComment.__typename === 'ReplyChangeProposalComment'
      ) {
        setCommentErrorMessage(null);
        onSuccess?.();
      } else {
        setCommentErrorMessage(
          assertUnreachableOrReturnDefault(
            resultProposal?.addComment,
            undefined,
          ),
        );
        onError?.();
      }
    },
    [],
  );

  return {
    addComment,
    handleAddCommentResult,
    commentErrorMessage,
    setCommentErrorMessage,
    loading,
  };
};
